import type { ScannedProduct } from '../types/purchase'

const PRICE_PATTERNS: RegExp[] = [
  /(?:US\$|\$)\s?(\d{1,4}(?:[.,]\d{3})*(?:[.,]\d{1,2})?)/,
  /(\d{1,4}(?:[.,]\d{1,2})?)\s?(?:USD|EUR|MXN)\b/i,
  /(?:USD|EUR|MXN)\s?(\d{1,4}(?:[.,]\d{1,2})?)/i
]

const CTA_PATTERN =
  /(add to cart|buy now|buy it|buy\b|checkout|order now|place order|comprar|c[oó]mpralo|a[ñn]adir al carrito|agregar al carrito|quiero|pagar)/i

const EDITION_PATTERN =
  /(pack\s+[\p{L}]+|edici[oó]n\s+digital|digital\s+edition|physical\s+book|libro\s+f[ií]sico|paperback|hardcover|volumen\s+\d+|bundle\s+completo|bundle|pack)/iu

const PRIMARY_PATTERN =
  /(bundle|pack|completo|physical|f[ií]sico|paperback|hardcover|5\s+ebooks)/i

const FALLBACK_PRODUCTS: ScannedProduct[] = [
  {
    id: 'fallback-physical',
    title: 'Pack Despertar',
    editionLabel: 'Physical Book',
    priceNumber: 27,
    priceText: '$27',
    tier: 'primary',
    source: 'fallback'
  },
  {
    id: 'fallback-digital',
    title: 'Edición Digital',
    editionLabel: 'Digital Edition',
    priceNumber: 17,
    priceText: '$17',
    tier: 'secondary',
    source: 'fallback'
  }
]

export function formatPrice(value: number): string {
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`
}

interface ParsedPrice {
  priceNumber: number
  priceText: string
}

export function parsePrice(text: string | null | undefined): ParsedPrice | null {
  if (!text) return null
  for (const pattern of PRICE_PATTERNS) {
    const match = pattern.exec(text)
    if (!match) continue
    const raw = match[1]
    if (!raw) continue
    const normalized = raw.includes(',') && !raw.includes('.')
      ? raw.replace(/\./g, '').replace(',', '.')
      : raw.replace(/,/g, '')
    const priceNumber = Number.parseFloat(normalized)
    if (!Number.isFinite(priceNumber) || priceNumber <= 0 || priceNumber > 5000) continue
    return { priceNumber, priceText: match[0].trim() }
  }
  return null
}

function tierFor(title: string, editionLabel: string): 'primary' | 'secondary' {
  return PRIMARY_PATTERN.test(`${title} ${editionLabel}`) ? 'primary' : 'secondary'
}

function cleanText(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim()
}

function editionFrom(text: string): string {
  const match = EDITION_PATTERN.exec(text)
  return match ? cleanText(match[0]) : ''
}

/* ---------------------------------- JSON-LD --------------------------------- */

function flattenJsonLd(node: unknown, out: Record<string, unknown>[]): void {
  if (Array.isArray(node)) {
    node.forEach(child => flattenJsonLd(child, out))
    return
  }
  if (!node || typeof node !== 'object') return
  const record = node as Record<string, unknown>
  out.push(record)
  if (record['@graph']) flattenJsonLd(record['@graph'], out)
  if (record.itemListElement) flattenJsonLd(record.itemListElement, out)
  if (record.item) flattenJsonLd(record.item, out)
}

function imageFrom(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return imageFrom(value[0])
  if (value && typeof value === 'object') {
    const url = (value as Record<string, unknown>).url
    if (typeof url === 'string') return url
  }
  return undefined
}

function isProductType(value: unknown): boolean {
  const types = Array.isArray(value) ? value : [value]
  return types.some(
    type => typeof type === 'string' && /^(product|book|individualproduct)$/i.test(type)
  )
}

function fromJsonLd(doc: Document): ScannedProduct[] {
  const products: ScannedProduct[] = []
  const scripts = Array.from(
    doc.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]')
  )

  scripts.forEach((script, scriptIndex) => {
    let parsed: unknown
    try {
      parsed = JSON.parse(script.textContent || '')
    } catch {
      return
    }
    const nodes: Record<string, unknown>[] = []
    flattenJsonLd(parsed, nodes)

    nodes.forEach((node, nodeIndex) => {
      if (!isProductType(node['@type'])) return
      const title = cleanText(typeof node.name === 'string' ? node.name : '')
      if (!title) return

      const thumbnailUrl = imageFrom(node.image)
      const rawOffers = node.offers
      const offers = (Array.isArray(rawOffers) ? rawOffers : [rawOffers]).filter(
        Boolean
      ) as Record<string, unknown>[]

      const declaredEdition = cleanText(
        typeof node.bookEdition === 'string' ? node.bookEdition : ''
      )

      if (offers.length === 0) return

      offers.forEach((offer, offerIndex) => {
        const rawPrice = offer.price ?? offer.lowPrice ?? offer.highPrice
        const priceNumber =
          typeof rawPrice === 'number'
            ? rawPrice
            : Number.parseFloat(String(rawPrice ?? '').replace(/[^\d.]/g, ''))
        if (!Number.isFinite(priceNumber) || priceNumber <= 0) return

        const offerName = cleanText(typeof offer.name === 'string' ? offer.name : '')
        const editionLabel =
          offerName || declaredEdition || editionFrom(title) || 'Edición Digital'

        products.push({
          id: `jsonld-${scriptIndex}-${nodeIndex}-${offerIndex}`,
          title,
          editionLabel,
          priceNumber,
          priceText: formatPrice(priceNumber),
          thumbnailUrl,
          tier: tierFor(title, editionLabel),
          source: 'json-ld'
        })
      })
    })
  })

  return products
}

/* ------------------------------------ DOM ----------------------------------- */

function findTitle(container: Element): string {
  const explicit = container.querySelector<HTMLElement>(
    '[data-product-title], [itemprop="name"]'
  )
  if (explicit) {
    const text = cleanText(explicit.textContent)
    if (text) return text
  }
  const headings = Array.from(
    container.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5')
  )
  for (const heading of headings) {
    const text = cleanText(heading.textContent)
    if (text.length >= 3 && text.length <= 80) return text
  }
  return ''
}

function findThumbnail(container: Element): string | undefined {
  const images = Array.from(container.querySelectorAll<HTMLImageElement>('img'))
  const withSrc = images.filter(image => image.currentSrc || image.src)
  if (withSrc.length === 0) return undefined
  const largest = withSrc.reduce((best, image) => {
    const area = (image.naturalWidth || image.width) * (image.naturalHeight || image.height)
    const bestArea =
      (best.naturalWidth || best.width) * (best.naturalHeight || best.height)
    return area > bestArea ? image : best
  }, withSrc[0])
  return largest.currentSrc || largest.src
}

function fromDom(root: ParentNode): ScannedProduct[] {
  const products: ScannedProduct[] = []
  const ctas = Array.from(
    root.querySelectorAll<HTMLElement>(
      'a, button, [role="button"], [data-product], [itemtype*="Product"]'
    )
  )

  ctas.forEach((cta, index) => {
    const ctaText = cleanText(cta.textContent)
    const isProductNode =
      cta.hasAttribute('data-product') ||
      (cta.getAttribute('itemtype') || '').includes('Product')
    if (!isProductNode && !CTA_PATTERN.test(ctaText)) return

    let container: Element | null = cta
    let price = parsePrice(ctaText)
    let depth = 0
    while (container && depth < 6) {
      const containerText = cleanText(container.textContent)
      const containerPrice = parsePrice(containerText)
      const title = findTitle(container)
      if (containerPrice && title) {
        price = containerPrice
        break
      }
      container = container.parentElement
      depth += 1
    }
    if (!container || !price) return

    const title = findTitle(container)
    if (!title) return

    const containerText = cleanText(container.textContent)
    const editionLabel = editionFrom(containerText) || editionFrom(title)

    products.push({
      id: `dom-${index}`,
      title,
      editionLabel,
      priceNumber: price.priceNumber,
      priceText: price.priceText,
      thumbnailUrl: findThumbnail(container),
      tier: tierFor(title, editionLabel),
      source: 'dom'
    })
  })

  return products
}

/* ---------------------------------- Public ---------------------------------- */

function dedupe(products: ScannedProduct[]): ScannedProduct[] {
  const seen = new Set<string>()
  return products.filter(product => {
    const key = `${product.title.toLowerCase()}|${product.priceNumber}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Builds a product catalog from the current page: structured data first,
 * then DOM heuristics, then a hardcoded fallback distribution.
 */
export function scanPageProducts(doc: Document = document): ScannedProduct[] {
  const structured = dedupe(fromJsonLd(doc));
  if (structured.length > 0) return structured.slice(0, 12)

  const domProducts = dedupe(fromDom(doc.body)).filter(
    product => product.title.length <= 80
  )
  if (domProducts.length > 0) return domProducts.slice(0, 12)

  return FALLBACK_PRODUCTS
}

export { FALLBACK_PRODUCTS }
